/* eslint complexity: ["error", { max: 10}] -- a lot of prop/conditional combinations */
import { useCallback, useMemo, useState } from "react";
import type { JSX, PropsWithChildren } from "react";

import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import { Accordion as BaseAccordion, Square } from "tamagui";

import { TextTestID } from "@/constants/TestID";
import Icon, { FAIconName } from "@/ui/core/Icon";
import Text from "@/ui/core/Text";
import { ColorToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

interface AccordionTriggerChevronProps extends ViewWithTestID {
  isOpen: boolean;
}

const AccordionTriggerChevron = ({
  isOpen,
  testID,
}: AccordionTriggerChevronProps) => {
  return (
    <Square rotate={isOpen ? "180deg" : "0deg"} transition="fade">
      <Icon
        color={ColorToken.Primary100}
        name={FAIconName.ChevronDown}
        size="sm"
        testID={joinTestIDs(testID, "chevron-down")}
      />
    </Square>
  );
};

interface DefaultTriggerProps extends ViewWithTestID {
  isOpen: boolean;
  title: string;
}

const DefaultTrigger = ({ isOpen, testID, title }: DefaultTriggerProps) => {
  return (
    <>
      <Text
        testID={joinTestIDs(testID, TextTestID.Title)}
        variant="captionMedium"
      >
        {title}
      </Text>
      <AccordionTriggerChevron isOpen={isOpen} testID={testID} />
    </>
  );
};

interface AccordionProps extends PropsWithChildren<ViewWithTestID> {
  backgroundColor?: string;
  borderWidth?: number;
  customTrigger?: JSX.Element;
  isDisabled?: boolean;
  isOpen?: boolean;
  onTriggerPress?: (isOpen: boolean) => void;
  paddingVertical?: number;
  shouldDefaultToOpen?: boolean;
  title: string;
}

const DefaultAccordionProps = {
  backgroundColor: ColorToken.Secondary5,
  borderWidth: 1,
  paddingVertical: 12,
} as const;

const AccordionBorderRadius = 12;

const Accordion = ({
  backgroundColor = DefaultAccordionProps.backgroundColor,
  borderWidth = DefaultAccordionProps.borderWidth,
  children,
  customTrigger,
  isDisabled = false,
  isOpen,
  onTriggerPress,
  paddingVertical = DefaultAccordionProps.paddingVertical,
  shouldDefaultToOpen = false,
  testID,
  title,
}: AccordionProps) => {
  const isParentControlled = !isNil(isOpen);
  const [isUncontrolledOpen, setIsUncontrolledOpen] =
    useState<boolean>(shouldDefaultToOpen);
  const isBaseAccordionOpen = isParentControlled ? isOpen : isUncontrolledOpen;
  const baseAccordionValue = isBaseAccordionOpen ? ["title"] : [];

  const handleAccordionValueChange = useCallback(
    (nextValue: string[]) => {
      if (isDisabled) return;
      const isNextValueOpen = nextValue.includes("title");
      if (!isParentControlled) {
        setIsUncontrolledOpen(isNextValueOpen);
      }
      onTriggerPress?.(isNextValueOpen);
    },
    [isDisabled, isParentControlled, onTriggerPress],
  );

  const defaultTrigger = useMemo(
    () => (
      <DefaultTrigger
        isOpen={isBaseAccordionOpen}
        testID={testID}
        title={title}
      />
    ),
    [isBaseAccordionOpen, testID, title],
  );

  const accordionTrigger = useMemo(() => {
    if (isNil(customTrigger)) return defaultTrigger;
    return customTrigger;
  }, [customTrigger, defaultTrigger]);

  // when open, merge header and content seamlessly via:
  //   - the header's bottom corners -> flat, no longer rounded
  //   - the content's bottom corners -> rounded
  const { borderBottomWidth, borderRadius } = isBaseAccordionOpen
    ? { borderBottomWidth: 0, borderRadius: 0 }
    : { borderBottomWidth: borderWidth, borderRadius: AccordionBorderRadius };

  return (
    <BaseAccordion
      onValueChange={handleAccordionValueChange}
      overflow="hidden"
      type="multiple"
      value={baseAccordionValue}
      width="100%"
    >
      <BaseAccordion.Item value="title">
        <BaseAccordion.Trigger
          backgroundColor={backgroundColor}
          // only dynamically round (or not) the bottom corners based on whether expanded/collapsed
          borderBottomLeftRadius={borderRadius}
          // only dynamically round (or not) the bottom corners based on whether expanded/collapsed
          borderBottomRightRadius={borderRadius}
          borderBottomWidth={borderBottomWidth}
          borderColor={backgroundColor}
          borderTopLeftRadius={AccordionBorderRadius}
          borderTopRightRadius={AccordionBorderRadius}
          borderWidth={borderWidth}
          disabled={isDisabled}
          flexDirection="row"
          // web-only: override Tamagui's default gray hover/focus/press with the base color (no-op on native)
          focusStyle={{ backgroundColor }}
          // web-only: override Tamagui's default gray hover/focus/press with the base color (no-op on native)
          hoverStyle={{ backgroundColor }}
          justifyContent="space-between"
          paddingHorizontal={18}
          paddingVertical={paddingVertical}
          // web-only: override Tamagui's default gray hover/focus/press with the base color (no-op on native)
          pressStyle={{ backgroundColor }}
        >
          {accordionTrigger}
        </BaseAccordion.Trigger>
        {!isEmpty(children) && (
          <BaseAccordion.HeightAnimator transition="fadeSlow">
            <BaseAccordion.Content
              backgroundColor={backgroundColor}
              borderBottomLeftRadius={AccordionBorderRadius}
              borderBottomRightRadius={AccordionBorderRadius}
              marginTop={-1}
              testID={joinTestIDs(testID, "content")}
              transition="fadeSlow"
            >
              {children}
            </BaseAccordion.Content>
          </BaseAccordion.HeightAnimator>
        )}
      </BaseAccordion.Item>
    </BaseAccordion>
  );
};

export default Accordion;
export { AccordionTriggerChevron };
export type { AccordionProps };
